import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import apiService from '../services/apiService';

// Conteúdo do grupo "Julgador smart" do AWON Monitor (julgador isento do plano, fase 4). O julgador roda nas máquinas
// do AWON a cada 2 horas (9h50 ... 21h50 e 23h50) e decide os atendimentos a partir dos recortes do worker1; a
// recorder-api confere e executa cada decisão e guarda a auditoria (awon_judge_round / awon_judge_command). Tudo é
// executado: o modo simulação saiu em 31/10, com o julgador em produção.
//   Decisões do período: "Para avaliação humana" (indecisos que esperam uma pessoa) e as decisões do julgador e das
//   pessoas — enviados ao n8n, reprovados. Recusa da API não é lista: volta ao julgador, que corrige ou deixa indeciso.
//   Indeciso resolvido não é lista (03/11): o desfecho dele está em Enviados ou Reprovados, uma vez.
//   Por rodada: as rodadas com as contagens, as decisões de cada uma e o freio.

const ATENDIMENTO = ['confirmar', 'ajustar', 'juntar', 'dividir', 'criar'];
const COMANDO = {
    confirmar: 'confirmou', ajustar: 'ajustou as bordas', juntar: 'juntou recortes', dividir: 'dividiu',
    criar: 'criou (sem recorte)', descartar: 'descartou', indeciso: 'deixou para uma pessoa',
};
// decisão de uma pessoa (comando sem rodada: botões do julgador no Monitor/MCP)
const COMANDO_PESSOA = { confirmar: 'aprovado por uma pessoa', descartar: 'descartado por uma pessoa',
    criar: 'criado por uma pessoa' };
const dePessoa = (c) => String(c.actor || '').startsWith('user:');
const MOTIVO = {
    colega: 'atendimento de colega', conversa_interna: 'conversa interna', nao_atendimento: 'não é atendimento',
    mesma_venda: 'mesma venda de outro recorte', sem_cliente: 'sem cliente', outro: 'outro',
};
const STATUS_RODADA = { rodando: 'rodando', concluida: 'concluída', falhou: 'falhou', desfeita: 'desfeita (freio)' };

const hora = (iso) => (iso ? String(iso).slice(11, 19) : '—');
// '2026-10-27' -> 'seg., 27/10' (dia da loja; meio-dia para não virar o dia pelo fuso do navegador)
const rotuloDia = (d) => {
    const x = new Date(`${d}T12:00:00`);
    return Number.isNaN(x.getTime()) ? d : x.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' });
};
const dataHora = (iso) => (iso ? `${String(iso).slice(8, 10)}/${String(iso).slice(5, 7)} ${String(iso).slice(11, 16)}` : '—');
const hojeLoja = () => new Date(Date.now() - 3 * 3600 * 1000).toISOString().slice(0, 10);

export function intervalo(periodo) {
    if (periodo?.date_start) return { date_start: periodo.date_start, date_end: periodo.date_end || periodo.date_start };
    const fim = hojeLoja();
    const ini = new Date(`${fim}T12:00:00Z`);
    ini.setUTCDate(ini.getUTCDate() - Math.max(0, (periodo?.days || 1) - 1));
    return { date_start: ini.toISOString().slice(0, 10), date_end: fim };
}

function conta(counts, filtro) {
    return Object.entries(counts || {}).reduce((n, [k, v]) => {
        const [cmd, st] = k.split('/');
        return filtro(cmd, st) ? n + v : n;
    }, 0);
}

// itens que esperam uma pessoa = o que a coluna "P/ revisão" dos clientes conta: cada recorte indeciso pendente (uma
// vez, mesmo citado por dois indecisos de rodadas diferentes) e cada indeciso sem recorte
function itensPendentes(lista) {
    const recortes = new Set();
    let semRecorte = 0;
    lista.forEach((c) => {
        const ids = c.session_ids || [];
        if (!ids.length) { semRecorte += 1; return; }
        (Array.isArray(c.pendentes) ? c.pendentes : ids).forEach((i) => recortes.add(i));
    });
    return recortes.size + semRecorte;
}

// as listas do plano, a partir das decisões (de uma rodada ou do período)
function listas(cmds) {
    const vivo = (c) => c.status !== 'recusado' && c.status !== 'desfeito';
    // indeciso ainda esperando uma pessoa (a API diz quais recortes seguem indecisos; sem o campo, como antes). Indeciso
    // SEM recorte (trecho que o worker1 não recortou, ou recusa da API que o julgador não corrigiu) também espera
    const pendente = (c) => (typeof c.espera_pessoa === 'boolean' ? c.espera_pessoa
        : (!(c.session_ids || []).length || !Array.isArray(c.pendentes) || c.pendentes.length > 0));
    // decidido depois (03/11): indeciso que não espera mais, ou reprovado que uma pessoa aprovou mesmo assim. Se quem
    // decidiu (rodada seguinte ou pessoa) já está nesta tela, ele some — a decisão aparece uma vez, na lista dela; senão
    // (rodada aberta sozinha, revisão do Monitor, "não é atendimento") entra na lista do desfecho, com quem decidiu
    const naTela = new Set(cmds.filter(vivo).map((c) => c.id));
    const depois = (c) => (c.command === 'indeciso' ? !pendente(c) : (c.decidido_por || []).length > 0);
    const jaNaTela = (c) => !(c.desfecho_fora || []).length && (c.decidido_por || []).length > 0
        && c.decidido_por.every((d) => naTela.has(d.id));
    const resolvidos = cmds.filter((c) => vivo(c) && ['indeciso', 'descartar'].includes(c.command) && depois(c)
        && !jaNaTela(c));
    const desfecho = (c) => c.desfecho || (c.command === 'descartar' ? 'no' : 'ok');
    return [
        // a ÚNICA lista que pede uma pessoa (= coluna "P/ revisão" dos clientes): os indecisos que ainda esperam —
        // o julgador não decidiu, recusa da API que ele não corrigiu ou recorte sem decisão no fechamento do dia. A
        // palavra final é do julgador: aprovados (enviados), reprovados ou indecisos; recusa da API não é lista (02/11)
        ['ind', 'Para avaliação humana (indecisos)', cmds.filter((c) => c.command === 'indeciso' && vivo(c) && pendente(c))],
        ['ok', 'Enviados ao n8n',
            cmds.filter((c) => ATENDIMENTO.includes(c.command) && vivo(c))
                .concat(resolvidos.filter((c) => desfecho(c) === 'ok'))],
        ['no', 'Reprovados',
            cmds.filter((c) => c.command === 'descartar' && vivo(c) && !depois(c))
                .concat(resolvidos.filter((c) => desfecho(c) === 'no'))],
        ['desf', 'Desfeitos', cmds.filter((c) => c.status === 'desfeito')],
    ].filter(([k, , l]) => k !== 'desf' || l.length > 0);
}

// "decidido por ...": quem resolveu um indeciso (ou aprovou mesmo assim um reprovado)
function decididoDepois(c) {
    const quem = (c.decidido_por || []).map((d) => (d.pessoa ? (COMANDO_PESSOA[d.command] || `${d.command} (pessoa)`)
        : `${COMANDO[d.command] || d.command} na rodada ${dataHora(d.round_slot).slice(6)}`));
    const fora = (c.desfecho_fora || []).map((f) => `${f.session_id ? `#${f.session_id}: ` : ''}${f.como}`);
    return [...new Set([...quem, ...fora])].join('; ');
}

// Um trecho do indeciso (o recorte ou o trecho sem recorte, com a margem): o áudio montado dos chunks, baixado só quando
// a pessoa clica (fica na memória da aba, sem link do S3 no navegador, sem download), e as falas que o julgador leu —
// clicar na hora de uma fala toca o áudio dali (o segundo da fala é o segundo do áudio)
function TrechoIndeciso({ subdomain, commandId, it, margem }) {
    const audio = useRef(null);
    const pular = useRef(null);
    const [src, setSrc] = useState(null);
    const [estado, setEstado] = useState('');
    useEffect(() => () => { if (src) URL.revokeObjectURL(src); }, [src]);
    const tocar = (seg) => {
        const a = audio.current;
        if (!a || seg == null) return;
        a.currentTime = seg;
        a.play().catch(() => {});
    };
    const ouvir = async (seg) => {
        if (src) { tocar(seg); return; }
        pular.current = seg ?? null;
        setEstado('carregando');
        try {
            const blob = await apiService.awonGetAudio(subdomain, { command_id: commandId, item: it.item });
            setSrc(URL.createObjectURL(blob));
            setEstado('');
        } catch (e) {
            setEstado('erro');
        }
    };
    const titulo = it.session_id ? `Recorte #${it.session_id}` : 'Trecho sem recorte';
    // a margem (antes e depois do recorte) vem separada por um marcador, com as mesmas cores do resto
    const iniSeg = (new Date(it.inicio) - new Date(it.de)) / 1000;
    const linhas = [];
    let lado = null;
    (it.falas || []).forEach((f, i) => {
        const agora = !f.fora ? 'dentro' : (f.seg < iniSeg ? 'antes' : 'depois');
        if (agora === 'dentro' && lado !== 'dentro') linhas.push({ marco: `início do recorte · ${hora(it.inicio)}`, k: `i${i}` });
        if (agora === 'depois' && lado === 'dentro') linhas.push({ marco: `fim do recorte · ${hora(it.fim)}`, k: `f${i}` });
        if (agora === 'depois' && (lado === null || lado === 'antes')) {
            linhas.push({ marco: `recorte ${hora(it.inicio)}–${hora(it.fim)} · sem fala`, k: `v${i}` });
        }
        linhas.push({ f, k: i });
        lado = agora;
    });
    if (lado === 'dentro') linhas.push({ marco: `fim do recorte · ${hora(it.fim)}`, k: 'fim' });
    if (lado === 'antes') linhas.push({ marco: `recorte ${hora(it.inicio)}–${hora(it.fim)} · sem fala`, k: 'vazio' });
    const minutos = margem >= 60 ? `${Math.round(margem / 60)} min` : `${margem} s`;
    return (
        <div className="awon-judge-trecho">
            <div className="awon-judge-trecho-cab">
                <strong>{titulo}</strong> · {hora(it.inicio)}–{hora(it.fim)}
                {!it.pendente && <span className="awon-muted"> · já decidido</span>}
            </div>
            <div className="awon-judge-trecho-audio">
                {src ? (
                    <audio ref={audio} controls autoPlay controlsList="nodownload" src={src}
                           onContextMenu={(e) => e.preventDefault()}
                           onLoadedMetadata={() => { if (pular.current != null) { tocar(pular.current); pular.current = null; } }}>
                        Seu navegador não suporta áudio.
                    </audio>
                ) : it.chunks > 0 ? (
                    <button type="button" className="awon-btn awon-btn-ghost awon-btn-sm" onClick={() => ouvir(null)}
                            disabled={estado === 'carregando'}>
                        {estado === 'carregando' ? 'Montando o áudio…' : '▶ Ouvir o trecho'}
                    </button>
                ) : <span className="awon-muted">Sem gravação da vendedora neste trecho.</span>}
                {estado === 'erro' && <span className="awon-muted"> não foi possível carregar o áudio — tente de novo</span>}
                {margem > 0 && <span className="awon-muted awon-judge-margem"> Áudio e falas com {minutos} antes e depois do recorte.</span>}
            </div>
            <div className="awon-transcription awon-judge-transcricao">
                <h4>Transcrição</h4>
                {linhas.length > 0 ? (
                    <div className="awon-diar-turns awon-judge-falas">
                        {linhas.map((l) => (l.marco ? (
                            <div key={l.k} className="awon-judge-marco">{l.marco}</div>
                        ) : (
                            <div key={l.k} className="awon-diar-turn">
                                {it.chunks ? (
                                    <button type="button" className="awon-diar-ts awon-judge-ts" title="Ouvir a partir desta fala"
                                            onClick={() => ouvir(l.f.seg)}>{l.f.hora}</button>
                                ) : <span className="awon-diar-ts">{l.f.hora}</span>}
                                <span className="awon-diar-spk" title={l.f.quem}>{l.f.quem}</span>
                                <span className="awon-diar-text">{l.f.texto}</span>
                            </div>
                        )))}
                    </div>
                ) : <p className="awon-muted">Sem fala transcrita neste trecho.</p>}
            </div>
        </div>
    );
}

// transcrição e áudio de um indeciso, para a pessoa decidir com o que o julgador leu
function MaterialIndeciso({ subdomain, commandId }) {
    const [mat, setMat] = useState(null);
    const [erro, setErro] = useState('');
    useEffect(() => {
        let vivo = true;
        setMat(null);
        setErro('');
        apiService.awonJudgeIndeciso(subdomain, commandId)
            .then((m) => { if (vivo) setMat(m); })
            .catch((e) => { if (vivo) setErro(e.message); });
        return () => { vivo = false; };
    }, [subdomain, commandId]);
    if (erro) return <p className="awon-muted">{erro}</p>;
    if (!mat) return <p className="awon-muted">Carregando a transcrição…</p>;
    if (!(mat.itens || []).length) return <p className="awon-muted">Sem trecho para mostrar.</p>;
    return (
        <div className="awon-judge-material">
            {mat.itens.map((it) => (
                <TrechoIndeciso key={it.item} subdomain={subdomain} commandId={commandId} it={it} margem={mat.margem_s} />
            ))}
        </div>
    );
}

// Botões de rodar uma rodada (só em QA): o mesmo código vai a PROD, mas a tela só mostra no admin-qa e a API só
// libera fora de PROD (APP_ENV). Cada horário da grade de hoje com a situação; só dá para pedir o que já passou,
// depois da última rodada que vale, um por vez — a worker de QA roda em até ~5 min.
const EM_QA = typeof window !== 'undefined' && /-qa\./.test(window.location.hostname || '');
const FORCAR_SITUACAO = {
    concluida: { txt: 'concluída', icone: '✓' }, rodando: { txt: 'rodando', icone: '⏳' },
    pedida: { txt: 'pedida', icone: '⏳' }, vencida: { txt: 'rodar agora', icone: '▶' },
    falhou: { txt: 'falhou — rodar de novo', icone: '↻' }, desfeita: { txt: 'desfeita — rodar de novo', icone: '↻' },
    pulada: { txt: 'já julgada por uma rodada depois', icone: '—' }, futura: { txt: 'ainda não é a hora', icone: '' },
};

const AwonJudgePanel = ({ subdomain, periodo, salesmen, indecisoLink }) => {
    const [visao, setVisao] = useState('periodo');
    const [vendedora, setVendedora] = useState('');
    const [dados, setDados] = useState(null);           // {rounds, progress}
    const [decisoes, setDecisoes] = useState(null);     // {commands, truncated}
    const [erro, setErro] = useState('');
    const [aberta, setAberta] = useState(null);         // round_id
    const [detalhe, setDetalhe] = useState(null);       // {round, commands}
    const [ocupado, setOcupado] = useState(false);
    const [confirmar, setConfirmar] = useState(null);   // {tipo: 'freio'|'desfazer'|'aprovar'|'descartar', id, texto}
    // listas recolhidas pelo título (enviados, reprovados, indecisos...), lembradas no navegador
    const [recolhidas, setRecolhidas] = useState(() => {
        try { return new Set(JSON.parse(localStorage.getItem('awonJudgeListasRecolhidas') || '[]')); } catch (e) { return new Set(); }
    });
    const recolher = (k, aberta) => setRecolhidas((atual) => {
        const novo = new Set(atual);
        if (aberta) novo.delete(k); else novo.add(k);
        try { localStorage.setItem('awonJudgeListasRecolhidas', JSON.stringify([...novo])); } catch (e) { /* sem storage */ }
        return novo;
    });

    // dias dentro de cada lista (visão do período): `${lista}|${dia}` -> aberto; sem escolha, só o dia mais recente abre
    const [diasAbertos, setDiasAbertos] = useState({});
    // indecisos com a transcrição e o áudio abertos (o do link ?indeciso= já vem aberto)
    const [materialAberto, setMaterialAberto] = useState(() => new Set(indecisoLink ? [indecisoLink] : []));
    const alternarMaterial = (id) => setMaterialAberto((atual) => {
        const novo = new Set(atual);
        if (novo.has(id)) novo.delete(id); else novo.add(id);
        return novo;
    });
    // botões de rodar uma rodada (QA): os horários de hoje por loja; atualiza a cada 20 s enquanto há pedido/rodada
    const [forcar, setForcar] = useState(null);
    const carregarForcar = useCallback(async () => {
        if (!EM_QA || !subdomain) return;
        try { setForcar(await apiService.awonJudgeForcar(subdomain)); } catch (e) { setForcar({ erro: e.message }); }
    }, [subdomain]);
    useEffect(() => { carregarForcar(); }, [carregarForcar]);
    // base de teste para hoje (QA): copia o molde para o dia; atualiza a cada 5 s enquanto copia
    const [base, setBase] = useState(null);
    const carregarBase = useCallback(async () => {
        if (!EM_QA || !subdomain) return;
        try { setBase(await apiService.awonJudgeBaseTeste(subdomain)); } catch (e) { setBase({ erro: e.message }); }
    }, [subdomain]);
    useEffect(() => { carregarBase(); }, [carregarBase]);
    const copiando = base?.andamento?.estado === 'copiando';
    useEffect(() => {
        if (!copiando) return undefined;
        const id = setInterval(carregarBase, 5000);
        return () => clearInterval(id);
    }, [copiando, carregarBase]);
    useEffect(() => { if (base?.hoje?.existe) carregarForcar(); }, [base?.hoje?.existe, carregarForcar]);
    const andando = (forcar?.lojas || []).some((l) => l.horarios.some((h) => ['pedida', 'rodando'].includes(h.situacao)));
    useEffect(() => {
        if (!andando) return undefined;
        const id = setInterval(() => { carregarForcar(); carregar(); }, 20000);
        return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [andando, carregarForcar]);
    // o indeciso do link (MCP / Slack): mostrado no topo, venha de que dia vier
    const [doLink, setDoLink] = useState(null);
    useEffect(() => {
        if (!indecisoLink || !subdomain) return;
        apiService.awonJudgeIndeciso(subdomain, indecisoLink)
            .then((m) => setDoLink(m.comando)).catch((e) => setDoLink({ erro: e.message }));
    }, [indecisoLink, subdomain]);

    const nomes = useMemo(() => Object.fromEntries((salesmen || []).map((s) => [s.id, s.name])), [salesmen]);
    const periodoApi = useMemo(() => intervalo(periodo), [periodo]);

    const carregar = useCallback(async () => {
        if (!subdomain) return;
        const filtros = { ...periodoApi };
        try {
            setErro('');
            const [r, c] = await Promise.all([
                apiService.awonJudgeRounds(subdomain, filtros),
                apiService.awonJudgeCommands(subdomain, { ...filtros, ...(vendedora ? { salesman_id: vendedora } : {}) }),
            ]);
            setDados(r);
            setDecisoes(c);
        } catch (e) {
            setErro(e.message);
        }
    }, [subdomain, periodoApi, vendedora]);

    useEffect(() => { setAberta(null); setDetalhe(null); carregar(); }, [carregar]);

    const abrir = useCallback(async (id) => {
        if (aberta === id) { setAberta(null); setDetalhe(null); return; }
        setAberta(id);
        setDetalhe(null);
        try {
            setDetalhe(await apiService.awonJudgeRound(subdomain, id));
        } catch (e) {
            setErro(e.message);
        }
    }, [aberta, subdomain]);

    const executar = async () => {
        const c = confirmar;
        setConfirmar(null);
        setOcupado(true);
        try {
            if (c.tipo === 'forcar') { await apiService.awonJudgeForcarPedir(subdomain, c.loja, c.horario); await carregarForcar(); return; }
            if (c.tipo === 'base') { await apiService.awonJudgeBaseTesteTrazer(subdomain); await carregarBase(); return; }
            if (c.tipo === 'aprovar' || c.tipo === 'descartar') await apiService.awonJudgeHuman(subdomain, c.id, c.tipo);
            else if (c.tipo === 'criar' || c.tipo === 'nao_atendimento') await apiService.awonJudgeHumanIntervalo(subdomain, c.id, c.tipo);
            else await apiService.awonJudgeUndo(subdomain, c.tipo === 'freio' ? { round_id: c.id } : { command_id: c.id });
            await carregar();
            if (aberta) setDetalhe(await apiService.awonJudgeRound(subdomain, aberta));
            if (indecisoLink) setDoLink((await apiService.awonJudgeIndeciso(subdomain, indecisoLink)).comando);
        } catch (e) {
            setErro(e.message);
        } finally {
            setOcupado(false);
        }
    };

    const rodadas = dados?.rounds || [];
    const julgado = useMemo(() => {   // "julgado até" mais recente de cada vendedora
        const out = {};
        (dados?.progress || []).forEach((p) => {
            const k = p.salesman_id;
            if (!out[k] || p.judged_until > out[k].judged_until) out[k] = p;
        });
        return Object.values(out).sort((a, b) => (nomes[a.salesman_id] || '').localeCompare(nomes[b.salesman_id] || ''));
    }, [dados, nomes]);
    const vendedorasComDecisao = useMemo(() => {
        const ids = new Set((decisoes?.commands || []).map((c) => c.salesman_id));
        (dados?.progress || []).forEach((p) => ids.add(p.salesman_id));
        return [...ids].sort((a, b) => (nomes[a] || '').localeCompare(nomes[b] || ''));
    }, [decisoes, dados, nomes]);
    // atendimento que o recorte gerou, na Revisão de pontos: a tela abre no dia, na vendedora e no atendimento
    const linkAtendimento = (a) => `/revisao-pontos?${new URLSearchParams({
        subdomain, store_id: a.store_id, salesman_id: a.salesman_id, date: String(a.dia).slice(0, 10),
        transcription_id: a.transcription_id,
    })}`;

    const linha = (c, comRodada) => {
        const prova = c.prova || {};
        const falhas = (c.checks || []).filter((x) => !x.ok && !x.aviso);
        const podeDesfazer = ['executado', 'falhou', 'aceito'].includes(c.status);
        // indeciso resolvido / reprovado aprovado mesmo assim, mostrado na lista do desfecho
        const depoisTxt = (c.command === 'indeciso' && c.espera_pessoa === false) || (c.command === 'descartar'
            && (c.decidido_por || []).length) ? decididoDepois(c) : '';
        const comMaterial = c.command === 'indeciso' && c.status !== 'desfeito' && c.status !== 'recusado';
        return (
            <React.Fragment key={c.id}>
            <tr className={c.status === 'desfeito' ? 'awon-judge-desfeito' : ''}>
                <td>{nomes[c.salesman_id] || `vendedora ${c.salesman_id}`}</td>
                <td className="awon-judge-hora">
                    {c.start_at ? `${dataHora(c.start_at).slice(0, 6)}${hora(c.start_at)}–${hora(c.end_at)}` : '—'}
                    {comRodada && <div className="awon-muted">{c.round_id ? `rodada ${dataHora(c.round_slot).slice(6)}` : 'decisão de uma pessoa'}</div>}
                </td>
                <td>
                    <strong>{(dePessoa(c) && COMANDO_PESSOA[c.command]) || COMANDO[c.command] || c.command}</strong>
                    {depoisTxt && <div className="awon-judge-depois">→ decidido por: {depoisTxt}</div>}
                    {c.venda && <span className="awon-badge awon-judge-venda" title="pagamento ouvido">venda</span>}
                    {c.command === 'descartar' && c.motivo && <div className="awon-muted">{MOTIVO[c.motivo] || c.motivo}{c.colega ? ` · ${c.colega}` : ''}</div>}
                    <div className="awon-judge-recortes">
                        {(c.session_ids || []).map((id) => <span key={id}>#{id}</span>)}
                        {(c.result_session_ids || []).length > 0 && (
                            <span> → novo {c.result_session_ids.map((id) => <span key={id}>#{id}</span>)}</span>
                        )}
                    </div>
                    {(c.atendimentos || []).map((a) => (
                        <a key={a.transcription_id} className="awon-judge-atendimento" href={linkAtendimento(a)}
                           target="_blank" rel="noopener noreferrer"
                           title="Abrir o atendimento que este recorte gerou na Revisão de pontos (aba nova)">
                            Ver atendimento #{a.transcription_id} ↗
                        </a>
                    ))}
                </td>
                <td>
                    {c.conduziu && <div><span className="awon-muted">conduziu:</span> {c.conduziu}</div>}
                    {(c.cliente_principal || []).length > 0 && (
                        <div><span className="awon-muted">cliente-principal:</span> {c.cliente_principal.join(', ')}</div>
                    )}
                    {c.nota && <div className="awon-judge-nota">{c.nota}</div>}
                    {(prova.abertura || prova.fechamento) && (
                        <details className="awon-judge-prova">
                            <summary>prova</summary>
                            {prova.abertura && <div>{prova.abertura.hora} “{prova.abertura.texto}”</div>}
                            {prova.fechamento && <div>{prova.fechamento.hora} “{prova.fechamento.texto}”</div>}
                        </details>
                    )}
                    {falhas.length > 0 && (
                        <ul className="awon-judge-falhas">{falhas.map((f, i) => <li key={i}>{f.check}: {f.detail}</li>)}</ul>
                    )}
                </td>
                <td>
                    <span className={`awon-badge awon-judge-st-${c.status}`}>{c.status}</span>
                    {['indeciso', 'descartar'].includes(c.command)
                        && ['executado', 'aceito'].includes(c.status) && !depoisTxt
                        && ((c.command === 'indeciso' && Array.isArray(c.pendentes) ? c.pendentes : c.session_ids) || []).map((id) => (
                        <span key={id} className="awon-judge-humano">
                            <button type="button" className="awon-btn awon-btn-sm awon-btn-ok" disabled={ocupado}
                                    onClick={() => setConfirmar({ tipo: 'aprovar', id,
                                        texto: `Aprovar o recorte #${id}? Ele vai ao n8n e vira atendimento (com pontos). A decisão fica registrada no seu nome e o julgador não mexe mais nele.` })}>
                                {c.command === 'descartar' ? `Aprovar mesmo assim #${id}` : `Aprovar #${id}`}
                            </button>
                            {c.command === 'indeciso' && (
                                <button type="button" className="awon-btn awon-btn-sm awon-btn-danger" disabled={ocupado}
                                        onClick={() => setConfirmar({ tipo: 'descartar', id,
                                            texto: `Descartar o recorte #${id}? Ele não vai ao n8n. A decisão fica registrada no seu nome.` })}>
                                    Descartar #{id}
                                </button>
                            )}
                        </span>
                    ))}
                    {c.command === 'indeciso' && !(c.session_ids || []).length && c.espera_pessoa && (
                        <span className="awon-judge-humano">
                            <button type="button" className="awon-btn awon-btn-sm awon-btn-ok" disabled={ocupado}
                                    onClick={() => setConfirmar({ tipo: 'criar', id: c.id,
                                        texto: `Criar o atendimento de ${nomes[c.salesman_id] || c.salesman_id} neste trecho (sem recorte do worker1)? O julgador monta o recorte em até ~5 min e ele vai ao n8n (com pontos). A decisão fica registrada no seu nome.` })}>
                                Criar atendimento
                            </button>
                            <button type="button" className="awon-btn awon-btn-sm awon-btn-danger" disabled={ocupado}
                                    onClick={() => setConfirmar({ tipo: 'nao_atendimento', id: c.id,
                                        texto: 'Marcar este trecho como "não é atendimento"? Nada vai ao n8n. A decisão fica registrada no seu nome.' })}>
                                Não é atendimento
                            </button>
                        </span>
                    )}
                    {comMaterial && (
                        <button type="button" className="awon-btn awon-btn-sm awon-btn-ghost awon-judge-ouvir"
                                aria-expanded={materialAberto.has(c.id)} onClick={() => alternarMaterial(c.id)}>
                            {materialAberto.has(c.id) ? 'Fechar transcrição e áudio' : '🎧 Transcrição e áudio'}
                        </button>
                    )}
                    {podeDesfazer && (
                        <button type="button" className="awon-btn awon-btn-sm awon-btn-danger" disabled={ocupado}
                                onClick={() => setConfirmar({ tipo: 'desfazer', id: c.id,
                                    texto: `Desfazer a decisão #${c.id} (${COMANDO[c.command] || c.command}) de ${nomes[c.salesman_id] || c.salesman_id}? O recorte volta ao que era antes; o atendimento criado por ela é arquivado.` })}>
                            Desfazer
                        </button>
                    )}
                </td>
            </tr>
            {comMaterial && materialAberto.has(c.id) && (
                <tr className="awon-judge-material-linha">
                    <td colSpan={5}><MaterialIndeciso subdomain={subdomain} commandId={c.id} /></td>
                </tr>
            )}
            </React.Fragment>
        );
    };

    const tabela = (lista, comRodada) => (
        <table className="awon-table">
            <thead><tr><th>Vendedora</th><th>Horário</th><th>Decisão</th><th>Por quê</th><th /></tr></thead>
            <tbody>{lista.map((c) => linha(c, comRodada))}</tbody>
        </table>
    );

    // visão do período: cada lista agrupada por dia do atendimento (o da rodada quando a decisão não tem horário),
    // do mais recente para o mais antigo, cada dia recolhível
    const porDia = (k, lista, comRodada) => {
        const grupos = [];
        lista.forEach((c) => {
            const d = String(c.start_at || c.round_slot || '').slice(0, 10) || 'sem data';
            const g = grupos.find((x) => x[0] === d);
            if (g) g[1].push(c); else grupos.push([d, [c]]);
        });
        grupos.sort((a, b) => b[0].localeCompare(a[0]));
        return grupos.map(([d, cs], i) => {
            const chave = `${k}|${d}`;
            const aberto = chave in diasAbertos ? diasAbertos[chave] : i === 0;
            const vendas = k === 'ok' ? cs.filter((c) => c.venda).length : 0;
            return (
                <details key={d} className="awon-judge-dia" open={aberto}
                         onToggle={(e) => { const v = e.currentTarget.open; if (v !== aberto) setDiasAbertos((a) => ({ ...a, [chave]: v })); }}>
                    <summary title={aberto ? 'Clique para recolher o dia' : 'Clique para abrir o dia'}>
                        <span className="awon-judge-dia-data">{rotuloDia(d)}</span> <span className="awon-count">{cs.length}</span>
                        {vendas > 0 && <span className="awon-muted"> · {vendas} com venda</span>}
                    </summary>
                    {tabela(cs, comRodada)}
                </details>
            );
        });
    };

    const blocoListas = (cmds, comRodada) => listas(cmds).map(([k, titulo, lista]) => (
        <details key={k} className={`awon-judge-lista awon-judge-lista-${k}`}
                 open={!recolhidas.has(k)}
                 onToggle={(e) => {
                     const aberta = e.currentTarget.open;
                     if (aberta === recolhidas.has(k)) recolher(k, aberta);
                 }}>
            <summary title={recolhidas.has(k) ? 'Clique para abrir a lista' : 'Clique para recolher a lista'}>
                <h3 className="awon-list-title">{titulo} <span className="awon-count">{k === 'ind' ? itensPendentes(lista) : lista.length}</span></h3>
            </summary>
            {lista.length === 0 ? <p className="awon-muted">Nenhuma.</p>
                : comRodada ? porDia(k, lista, comRodada) : tabela(lista, comRodada)}
        </details>
    ));

    return (
        <div className="awon-judge">
            {indecisoLink && (
                <div className="awon-judge-link">
                    <h3 className="awon-list-title">Indeciso #{indecisoLink} <span className="awon-muted">— aberto pelo link</span></h3>
                    {!doLink ? <p className="awon-muted">Carregando…</p>
                        : doLink.erro ? <p className="awon-muted">{doLink.erro}</p> : tabela([doLink], true)}
                </div>
            )}
            {EM_QA && forcar?.habilitado && (
                <div className="awon-judge-forcar">
                    <h3 className="awon-list-title">Rodar uma rodada agora <span className="awon-badge">QA</span></h3>
                    <p className="awon-muted">
                        Só em QA. Cada botão roda a rodada daquele horário: o julgador julga do último "julgado até" até meia hora
                        antes do horário, e os aprovados vão ao n8n de QA. A worker de QA roda em até ~5 min (liga sozinha se
                        estiver desligada). Só dá para rodar horário que já passou, um por vez; para refazer uma, use o freio dela
                        em "Por rodada" e rode de novo.
                    </p>
                    {base?.habilitado && (() => {
                        const m = base.molde || {};
                        const a = base.andamento;
                        const dia = (base.dia || '').split('-').reverse().slice(0, 2).join('/');
                        return (
                            <div className="awon-judge-base">
                                <strong>Base de teste de hoje ({dia}):</strong>{' '}
                                {base.hoje?.existe ? (
                                    <span>pronta — {base.hoje.recortes} recortes do worker1 ({m.descricao}).</span>
                                ) : copiando ? (
                                    <span>⏳ copiando… {a.feitos || 0} de {a.total || '?'} arquivos (alguns minutos).</span>
                                ) : (
                                    <>
                                        {a?.estado === 'falhou' && <span className="awon-judge-base-erro">a cópia falhou ({a.erro}). </span>}
                                        <button type="button" className="awon-btn awon-btn-sm awon-btn-ok" disabled={ocupado}
                                                onClick={() => setConfirmar({ tipo: 'base',
                                                    texto: `Trazer para hoje (${dia}) a base de teste: ${m.descricao} — ${m.recortes} recortes do worker1 de ${(m.vendedoras || []).join(', ')}, em revisão e sem decisão do julgador. É uma cópia nova: os dias anteriores não mudam. Leva alguns minutos; depois a loja aparece nos botões abaixo e cada horário libera quando a hora passa.` })}>
                                            Trazer a base de teste para hoje
                                        </button>
                                    </>
                                )}
                            </div>
                        );
                    })()}
                    {base?.erro && <p className="awon-muted">{base.erro}</p>}
                    {(forcar.lojas || []).length === 0 && <p className="awon-muted">Nenhuma loja com gravação hoje.</p>}
                    {(forcar.lojas || []).map((l) => (
                        <div key={l.store_id} className="awon-judge-forcar-loja">
                            <div className="awon-judge-forcar-nome">{l.loja || `loja ${l.store_id}`}</div>
                            <div className="awon-judge-forcar-botoes">
                                {l.horarios.map((h) => {
                                    const sit = FORCAR_SITUACAO[h.situacao] || { txt: h.situacao, icone: '' };
                                    const dica = h.pedido?.status === 'falhou' && h.pedido.erro ? `${sit.txt} · ${h.pedido.erro}`
                                        : h.pedido?.por ? `${sit.txt} · pedida por ${h.pedido.por}` : sit.txt;
                                    return (
                                        <button key={h.horario} type="button" title={`${dica} — julga até ${h.trecho_ate}`}
                                                className={`awon-btn awon-btn-sm awon-judge-forcar-${h.situacao}`}
                                                disabled={!h.pode_pedir || ocupado}
                                                onClick={() => setConfirmar({ tipo: 'forcar', loja: l.store_id, horario: h.horario,
                                                    texto: `Rodar agora a rodada das ${h.horario} (${l.loja || `loja ${l.store_id}`})? O julgador decide os recortes de cada vendedora do último "julgado até" até ${h.trecho_ate}, e os aprovados vão ao n8n de QA e viram atendimentos com pontos. A worker de QA roda em até ~5 min.` })}>
                                            {sit.icone} {h.horario}
                                            <span className="awon-judge-forcar-sit">{sit.txt}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                    {forcar.erro && <p className="awon-muted">{forcar.erro}</p>}
                </div>
            )}
            <div className="awon-judge-filtros">
                <div className="awon-judge-visoes" role="tablist">
                    {[['periodo', 'Decisões do período'], ['rodadas', 'Por rodada']].map(([k, t]) => (
                        <button key={k} type="button" role="tab" aria-selected={visao === k}
                                className={`awon-btn awon-btn-sm ${visao === k ? 'awon-judge-visao-on' : ''}`}
                                onClick={() => setVisao(k)}>{t}</button>
                    ))}
                </div>
                {visao === 'periodo' && (
                    <select value={vendedora} onChange={(e) => setVendedora(e.target.value)}>
                        <option value="">Todas as vendedoras</option>
                        {vendedorasComDecisao.map((id) => <option key={id} value={id}>{nomes[id] || id}</option>)}
                    </select>
                )}
                <button type="button" className="awon-btn awon-btn-sm" onClick={carregar} disabled={ocupado}>Atualizar</button>
            </div>
            <p className="awon-muted">
                Roda a cada 2 horas (9h50, 11h50 … 21h50 e o fechamento às 23h50) e decide os atendimentos de cada vendedora
                a partir dos recortes do worker1. Tudo que ele decide é executado.
            </p>
            {erro && <p className="awon-error">{erro}</p>}

            {julgado.length > 0 && (
                <div className="awon-judge-julgado">
                    <span className="awon-muted">Julgado até:</span>
                    {julgado.map((p) => (
                        <span key={p.salesman_id} className="awon-badge" title={`rodada #${p.round_id}`}>
                            {nomes[p.salesman_id] || p.salesman_id} {dataHora(p.judged_until)}
                        </span>
                    ))}
                </div>
            )}

            {visao === 'periodo' && (
                !decisoes ? <p className="awon-muted">Carregando…</p> : (
                    <>
                        {decisoes.truncated && <p className="awon-muted">Mostrando as 3.000 decisões mais recentes do período.</p>}
                        {blocoListas(decisoes.commands || [], true)}
                    </>
                )
            )}

            {visao === 'rodadas' && (!dados ? <p className="awon-muted">Carregando…</p> : rodadas.length === 0 ? (
                <p className="awon-muted">Nenhuma rodada no período.</p>
            ) : (
                <table className="awon-table awon-judge-rodadas">
                    <thead>
                        <tr>
                            <th>Rodada</th><th>Status</th><th>Vendedoras</th><th>Ao n8n</th>
                            <th>Reprovados</th><th>Indecisos</th><th>Conflitos</th><th>Custo</th>
                            <th>Duração</th><th />
                        </tr>
                    </thead>
                    <tbody>
                        {rodadas.map((r) => (
                            <React.Fragment key={r.id}>
                                <tr className={aberta === r.id ? 'awon-judge-aberta' : ''}>
                                    <td>{dataHora(r.slot)} <span className="awon-muted">#{r.id}</span></td>
                                    <td title={r.error || ''}>
                                        <span className={`awon-badge awon-judge-rd-${r.status}`}>{STATUS_RODADA[r.status] || r.status}</span>
                                        {r.undone_by && <div className="awon-muted">por {r.undone_by}</div>}
                                    </td>
                                    <td>{r.sellers ?? '—'}</td>
                                    <td>{conta(r.counts, (cmd, st) => ATENDIMENTO.includes(cmd) && !['recusado', 'desfeito'].includes(st))}</td>
                                    <td>{conta(r.counts, (cmd, st) => cmd === 'descartar' && !['recusado', 'desfeito'].includes(st))}</td>
                                    <td>{conta(r.counts, (cmd, st) => cmd === 'indeciso' && !['recusado', 'desfeito'].includes(st))}</td>
                                    <td>{r.conflicts ?? 0}</td>
                                    <td>{r.cost_usd != null ? `US$ ${Number(r.cost_usd).toFixed(2)}` : '—'}</td>
                                    <td>{r.duration_s != null ? `${Math.round(r.duration_s)} s` : '—'}</td>
                                    <td className="awon-judge-acoes">
                                        <button type="button" className="awon-btn awon-btn-sm" onClick={() => abrir(r.id)}>
                                            {aberta === r.id ? 'Fechar' : 'Ver decisões'}
                                        </button>
                                        {r.status === 'concluida' && (
                                            <button type="button" className="awon-btn awon-btn-sm awon-btn-danger" disabled={ocupado}
                                                    title="Desfaz todas as decisões executadas desta rodada"
                                                    onClick={() => setConfirmar({ tipo: 'freio', id: r.id,
                                                        texto: `Acionar o freio da rodada das ${dataHora(r.slot)}? Todas as decisões executadas voltam ao que eram antes: os recortes voltam à lista do worker1 e os atendimentos criados pelo julgador são arquivados.` })}>
                                                Freio
                                            </button>
                                        )}
                                    </td>
                                </tr>
                                {aberta === r.id && (
                                    <tr className="awon-judge-detalhe">
                                        <td colSpan={12}>
                                            {!detalhe ? <p className="awon-muted">Carregando decisões…</p> : (
                                                <>
                                                    {(detalhe.round?.conflicts || []).length > 0 && (
                                                        <div className="awon-judge-conflitos">
                                                            <strong>Conflitos entre vendedoras</strong>
                                                            <ul>{detalhe.round.conflicts.map((c, i) => (
                                                                <li key={i}>
                                                                    {c.janela?.join('–')} · {nomes[c.a?.[0]] || c.a?.[0]}
                                                                    {c.b ? ` × ${nomes[c.b[0]] || c.b[0]}` : ''} · <strong>{c.decisao}</strong>
                                                                    {c.motivo ? ` — ${c.motivo}` : ''}
                                                                </li>
                                                            ))}</ul>
                                                        </div>
                                                    )}
                                                    {blocoListas(detalhe.commands || [], false)}
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            ))}

            {confirmar && (
                <div className="awon-modal-overlay" role="dialog" aria-modal="true">
                    <div className="awon-modal">
                        <h3 className="awon-modal-title">{{ freio: 'Freio da rodada', desfazer: 'Desfazer decisão',
                            aprovar: 'Aprovar recorte', descartar: 'Descartar recorte', criar: 'Criar atendimento',
                            nao_atendimento: 'Não é atendimento', forcar: 'Rodar a rodada agora (QA)',
                            base: 'Base de teste para hoje (QA)' }[confirmar.tipo]}</h3>
                        <p>{confirmar.texto}</p>
                        <div className="awon-modal-actions">
                            <button type="button" className="awon-btn" onClick={() => setConfirmar(null)}>Cancelar</button>
                            <button type="button" className={`awon-btn ${['aprovar', 'criar', 'forcar', 'base'].includes(confirmar.tipo) ? 'awon-btn-ok' : 'awon-btn-danger'}`}
                                    onClick={executar}>
                                {{ freio: 'Acionar o freio', desfazer: 'Desfazer', aprovar: 'Aprovar', descartar: 'Descartar',
                                    criar: 'Criar atendimento', nao_atendimento: 'Não é atendimento', forcar: 'Rodar agora',
                                    base: 'Trazer a base' }[confirmar.tipo]}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AwonJudgePanel;
